import React, { FC, useRef } from 'react';
import { View } from 'react-native';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { NextVideoOverlay, RadialGradientBackground, TWCPage, VideoPlayer } from '@components';

import { appHeight, appWidth, fullscreenVODPageTestIDs, height, VODControlsTestIDs, width } from '@constants';
import { helpers } from '@helpers';
import { useAppContextReference } from '@hooks';

import { useFullScreen } from './hooks';
import { styles } from './styles';
import { Stack, VideoPlayerReference, VODPlaylistReference } from './types';
import { useGetVODVideo } from './useGetVODVideo';

export const FullscreenVODPage: FC = () => {
  const isScreenFocused = useIsFocused();

  const vodPlaylistReference = useRef<VODPlaylistReference | null>(null);
  const videoPlayerReference = useRef<VideoPlayerReference>(null);
  const vodFirstFocusReference = useAppContextReference(VODControlsTestIDs.vodVideoControls);

  const { params } = useRoute<RouteProp<Stack, 'FullscreenVODPage'>>();

  const {
    videoID,
    seasonID,
    needsBookmarking,
    startPosition,
    vodID,
    nextEpisodeData: { countdown, duration, title },
    showNextEpisodeOverlay,
    onVideoEndResult,
    onVideoProgressResult,
  } = useFullScreen(params, videoPlayerReference, vodPlaylistReference, vodFirstFocusReference);

  const { data, error, isLoading } = useGetVODVideo(vodID ?? '');

  const onPageFocus = () => {
    helpers.FOCUS_NOW(vodFirstFocusReference);
    return false;
  };

  const assignReference = (reference: VODPlaylistReference) => {
    vodPlaylistReference.current = reference;
  };

  const videoPlayerData = {
    ...data,
    seasonID,
    vodID,
    videoID,
    needsBookmarking,
    startPosition,
    disableInputs: showNextEpisodeOverlay,
  };

  return (
    <TWCPage childIds={[]} pageID={fullscreenVODPageTestIDs.fullscreenVODPage} onPageFocus={onPageFocus}>
      <View style={styles.twcPageContainer}>
        <>
          {isScreenFocused ? (
            <RadialGradientBackground>
              <NextVideoOverlay
                countdown={Math.round(countdown ?? 0)}
                goToNextVideo={onVideoEndResult}
                nextDuration={duration}
                nextTitle={title}
                overlayDuration={8}
                shouldShow={showNextEpisodeOverlay}
              />
              <VideoPlayer
                adData={null}
                data={videoPlayerData}
                error={error}
                height={helpers.isWeb ? appHeight : height}
                isFullScreen // android player uses this
                isFullscreen // web player uses this. Should consolidate!
                isLoading={isLoading}
                isScreenFocused={isScreenFocused}
                nextFocusUp={null}
                ref={videoPlayerReference}
                vodPlaylistReference={assignReference}
                width={helpers.isWeb ? appWidth : width}
                onVideoEnd={onVideoEndResult}
                onVideoProgress={onVideoProgressResult}
              />
            </RadialGradientBackground>
          ) : null}
        </>
      </View>
    </TWCPage>
  );
};
